import React from 'react';
import {createStyles, makeStyles, Theme} from '@material-ui/core/styles';
import {useTranslation} from "react-i18next";
import {useParams} from 'react-router-dom';
import {useSnackbar} from "notistack";
import {IShipment} from "../core/shipments/ShipmentList";
import {getShipmentInfo, getShipmentSummary} from "../../api/ShipmentEndpoint";
import {RR_STATUS} from "../../constants/RRConstants";
import {IShipmentSummary} from "../../pages/SingleShipmentTrackingPage";
import ShipmentInfoCard from './ShipmentInfoCard';
import Grid from '@material-ui/core/Grid';
import RoutingPointsStepper from "../core/shipments/RoutingPointsStepper";
import Loader from "../core/Loader";
import {Container, Paper} from '@material-ui/core';
import RoutingPointInfoList from './RoutingPointInfoList';
import CommentsList from "../core/comments/CommentsList";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center"
        },
        grid: {
            "& .MuiGrid-item": {
                justifyContent: "center",
                alignItems: "center",
                display: "flex",
                flexDirection: "column"
            }
        },

    })
);

interface SingleShipmentTrackingProps {
    setShipment: (shipment: IShipment | undefined) => void
}

export const SingleShipmentTrackingComponents: React.FC<SingleShipmentTrackingProps> = ({ setShipment }) => {
    const classes = useStyles();
    const {enqueueSnackbar} = useSnackbar();
    const {t} = useTranslation();

    const {uuid} = useParams<{ uuid: string }>();

    const shipmentUuid = uuid!;

    const [shipmentSummary, setShipmentSummary] = React.useState<IShipmentSummary | undefined>(undefined);
    const [shipmentInfo, setShipmentInfo] = React.useState<IShipment | undefined>(undefined);

    React.useEffect(() => {
        getShipmentSummary(shipmentUuid).then(({data}) => {
            const {status, response} = data;

            if (status !== RR_STATUS.OK) {
                enqueueSnackbar(t("core.server-error"), {
                    variant: "error",
                    preventDuplicate: false
                });
            } else {
                setShipmentSummary(response);
            }
        });

        getShipmentInfo(shipmentUuid).then(({data}) => {
            const {status, response} = data;

            if (status !== RR_STATUS.OK) {
                enqueueSnackbar(t("core.server-error"), {
                    variant: "error",
                    preventDuplicate: false
                });
            } else {
                setShipmentInfo(response);
                setShipment(response);
            }
        });
    }, [enqueueSnackbar, t, setShipmentSummary, setShipment, shipmentUuid]);

    return (<div className={classes.root}>
            <Container>
                <Grid container justifyContent="center"
                      alignItems="center" spacing={2}>

                    <Grid item xs={12}>
                        <ShipmentInfoCard shipmentSummary={shipmentSummary}/>
                    </Grid>
                    <Grid item xs={12}>
                        <RoutingPoints shipment={shipmentInfo}/>
                    </Grid>
                    <Grid item xs={12}>
                        <CommentsList shipmentId={shipmentInfo?.shipmentId}/>
                    </Grid>
                </Grid>
            </Container>
        </div>
    );
};


const RoutingPoints: React.FC<{ shipment?: IShipment }> = ({shipment}) => {

    const classes = useStyles();

    if (!shipment) {
        return <Loader/>;
    }

    const {routingPoints} = shipment;

    return <Paper className={classes.root}>
        <Grid container justifyContent="center"
              alignItems="center">
            <Grid item xs={12} sm={6}>
                <RoutingPointsStepper routingPoints={routingPoints} isVertical={true}/>
            </Grid>

            <Grid item xs={12} sm={6}>
                <RoutingPointInfoList shipmentId={shipment?.shipmentId}/>
            </Grid>

        </Grid>
    </Paper>
};


export default SingleShipmentTrackingComponents;